import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Settings, Rocket, Users } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const primaryColor = "#43bd98";

const HowItWorks = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 640px)");

	const steps = [
		{
			icon: <Settings size={28} strokeWidth={1.8} />,
			title: t("howItWorksNew.steps.0.title"),
			description: t("howItWorksNew.steps.0.description"),
		},
		{
			icon: <Rocket size={28} strokeWidth={1.8} />,
			title: t("howItWorksNew.steps.1.title"),
			description: t("howItWorksNew.steps.1.description"),
		},
		{
			icon: <Users size={28} strokeWidth={1.8} />,
			title: t("howItWorksNew.steps.2.title"),
			description: t("howItWorksNew.steps.2.description"),
		},
	];

	return (
		<section
			style={{
				padding: isMobile ? "4rem 1.5rem" : "5rem 2rem",
				backgroundColor: "#ffffff",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div style={{ maxWidth: "900px", margin: "0 auto" }}>
				{/* Header */}
				<motion.div
					style={{ textAlign: "center", marginBottom: "3rem" }}
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
				>
					<p
						style={{
							color: primaryColor,
							fontSize: "0.9rem",
							fontWeight: 600,
							letterSpacing: "1px",
							textTransform: "uppercase",
							marginBottom: "0.5rem",
						}}
					>
						{t("howItWorksNew.badge")}
					</p>
					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
							color: "#1a1a1a",
							fontWeight: 800,
							margin: 0,
						}}
					>
						{t("howItWorksNew.title")}
					</h2>
				</motion.div>

				{/* Steps */}
				<div
					style={{
						display: "flex",
						flexDirection: isMobile ? "column" : "row",
						gap: isMobile ? "1.5rem" : "1rem",
						position: "relative",
					}}
				>
					{/* Connection line (desktop only) */}
					{!isMobile && (
						<div
							style={{
								position: "absolute",
								top: "45px",
								left: "calc(16.66% + 20px)",
								right: "calc(16.66% + 20px)",
								height: "2px",
								background: `linear-gradient(90deg, ${primaryColor}40 0%, ${primaryColor} 50%, ${primaryColor}40 100%)`,
								zIndex: 0,
							}}
						/>
					)}

					{steps.map((step, index) => (
						<motion.div
							key={index}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: index * 0.15 }}
							viewport={{ once: true }}
							style={{
								flex: 1,
								textAlign: "center",
								position: "relative",
								zIndex: 1,
							}}
						>
							{/* Step number badge */}
							<div
								style={{
									width: "70px",
									height: "70px",
									borderRadius: "50%",
									background: "#fff",
									border: `3px solid ${primaryColor}`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									margin: "0 auto 1.25rem",
									color: primaryColor,
									boxShadow: "0 4px 15px rgba(67,189,152,0.2)",
								}}
							>
								{step.icon}
							</div>

							<h3
								style={{
									fontSize: "1.1rem",
									fontWeight: 700,
									color: "#1f2937",
									marginBottom: "0.5rem",
								}}
							>
								{step.title}
							</h3>

							<p
								style={{
									color: "#6b7280",
									fontSize: "0.9rem",
									lineHeight: 1.6,
									maxWidth: "250px",
									margin: "0 auto",
								}}
							>
								{step.description}
							</p>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
};

export default HowItWorks;
